import { supportLabels } from "@/i18n";
import { getLanguage, type Language } from "@/utils/languageUtils";
import type { ComplaintFollowUpCategory } from "@/components/support/complaintFollowUpCatalog";

type OptionMap = Record<string, string>;

function followUpGroup(
  lang?: Language,
): Record<string, Record<string, OptionMap>> {
  return (supportLabels(lang).followUpOptions ?? {}) as Record<
    string,
    Record<string, OptionMap>
  >;
}

export function followUpOptionLabel(
  category: string,
  kind: string,
  id: string | undefined,
  lang?: Language,
): string {
  if (!id) return "";
  return followUpGroup(lang)?.[category]?.[kind]?.[id] ?? id;
}

type FollowUpRow = { label: string; value: string };

export function getComplaintFollowUpRows(
  category: string | undefined,
  values: {
    damageLocation?: string;
    damageType?: string;
    engineComponent?: string;
    interiorSection?: string;
    wheelType?: string;
  },
  lang?: Language,
): FollowUpRow[] {
  if (!category) return [];
  const labels = supportLabels(lang);
  const currentLang = lang || getLanguage();
  const rows: FollowUpRow[] = [];

  const push = (label: string | undefined, value: string) => {
    if (!value) return;
    rows.push({ label: label || "", value });
  };

  switch (category as ComplaintFollowUpCategory) {
    case "body": {
      const q = labels.bodyDamage ?? {};
      push(
        q.whereIsDamage,
        followUpOptionLabel("body", "parts", values.damageLocation, currentLang),
      );
      push(
        q.whatDamageType,
        followUpOptionLabel("body", "damageTypes", values.damageType, currentLang),
      );
      break;
    }
    case "engine": {
      const q = labels.engine ?? {};
      push(
        q.whichComponent,
        followUpOptionLabel("engine", "components", values.engineComponent, currentLang),
      );
      push(
        q.whatProblemType,
        followUpOptionLabel("engine", "problems", values.damageType, currentLang),
      );
      break;
    }
    case "interior": {
      const q = labels.interior ?? {};
      push(
        q.whichPart,
        followUpOptionLabel("interior", "sections", values.interiorSection, currentLang),
      );
      push(
        q.specifyLocation,
        followUpOptionLabel("interior", "locations", values.damageLocation, currentLang),
      );
      push(
        q.whatDamageType,
        followUpOptionLabel("interior", "damageTypes", values.damageType, currentLang),
      );
      break;
    }
    case "underbody": {
      const q = labels.underbody ?? {};
      push(
        q.whichPart,
        followUpOptionLabel("underbody", "parts", values.damageLocation, currentLang),
      );
      push(
        q.whatDamageType,
        followUpOptionLabel("underbody", "damageTypes", values.damageType, currentLang),
      );
      break;
    }
    case "tires": {
      const q = labels.tires ?? {};
      push(
        q.whichWheelType,
        followUpOptionLabel("tires", "wheelTypes", values.wheelType, currentLang),
      );
      push(
        q.whichWheel,
        followUpOptionLabel("tires", "positions", values.damageLocation, currentLang),
      );
      push(
        q.whatDamageType,
        followUpOptionLabel("tires", "damageTypes", values.damageType, currentLang),
      );
      break;
    }
    case "brakes": {
      const q = labels.brakes ?? {};
      push(
        q.whichBrake,
        followUpOptionLabel("brakes", "positions", values.damageLocation, currentLang),
      );
      push(
        q.whatDamageType,
        followUpOptionLabel("brakes", "damageTypes", values.damageType, currentLang),
      );
      break;
    }
    default:
      break;
  }

  return rows;
}
